import {
  Component,
  HostBinding,
  Inject,
  OnInit,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  PL_API_CONFIG,
  PlAPIConfig,
  PlAuthService,
  PLAuthState,
  selectAuth,
  User,
} from 'pl-core-services';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { PlImpersonationService } from '../pl-core-services/pl-auth/services/pl-impersonation.service';
import { selectIsImpersonating } from '../pl-core-services/pl-auth/store';

@Component({
  selector: 'pl-hijack',
  templateUrl: 'hijack.component.html',
  styleUrls: ['./hijack.component.scss'],
})
export class HijackComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  hijacked = false;

  isOidcImpersonation = false;

  @HostBinding('class.open')
  hijackOpened = true;

  user: User;

  private legacyReleaseUrl: string;

  constructor(
    @Inject(PL_API_CONFIG) private config: PlAPIConfig,
    private store: Store,
    private authService: PlAuthService,
    private httpClient: HttpClient,
    private impersonationService: PlImpersonationService,
    private router: Router
  ) {
    this.legacyReleaseUrl = `${config.django_login_url}/hijack/release-hijack/`;
  }

  ngOnInit() {
    // Subscribe to OIDC impersonation state reactively
    this.store
      .select(selectIsImpersonating)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isImpersonating) => {
        this.isOidcImpersonation = isImpersonating;
      });

    // Subscribe to legacy hijack state
    this.store
      .select(selectAuth)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authState: PLAuthState) => {
        this.hijacked = authState.hijacked;
        this.user = authState.currentUser;
      });
  }

  get username() {
    return this.user?.username;
  }

  get releaseUrl() {
    // Return the appropriate URL based on which system is active
    return this.isOidcImpersonation
      ? '/auth/end-impersonation'
      : this.legacyReleaseUrl;
  }

  @HostBinding('style.display')
  get styleDisplay() {
    // Show banner for either legacy hijack or OIDC impersonation
    return this.hijacked || this.isOidcImpersonation ? null : 'none';
  }

  onClickRelease() {
    // Handle OIDC impersonation
    if (this.isOidcImpersonation) {
      this.router.navigate([this.releaseUrl]);
      return;
    }

    // Handle legacy hijack
    this.httpClient.get(this.releaseUrl, { withCredentials: true }).subscribe({
      next: () => this.gotoAdmin(),
      error: (e) => {
        // release-hijack redirects and causes a CORS error,
        // so we get user status to see if the release worked
        if (e.status === 0) {
          this.authService.getUserStatus().subscribe(({ user }) => {
            if (user.uuid !== this.user.uuid) {
              this.gotoAdmin();
            }
          });
        }
        // rethrow since we didn't handle the error very neatly
        throw e;
      },
    });
  }

  gotoAdmin() {
    window.location.assign(`${this.config.django_login_url}/admin`);
  }
}
